import type { Exercise } from "./schemas/exercise.js";
import type { CatalogMuscleSlug } from "./muscleCatalog.js";
import type { TargetItem } from "./schemas/program.js";
import type { TimedSessionResult } from "./progressTypes.js";
import {
  catalogBySlug,
  normalizeVolumeByMuscle,
  resolveExerciseMuscleCredits,
} from "./volumeByMuscle.js";
import { calendarWeeksAgo, DEFAULT_WEEK_START_DAY } from "./weekWindow.js";

// AP target actuals (2026-07-07). The numbers that ground a weekly target: what
// the athlete is ACTUALLY doing, per (axis, key, metric), so the editor shows a
// reference ("you average 12/week, last week 15") instead of asking for a goal
// in a vacuum. Also the source of the meter's muscle hard-set actual. All pure,
// reading the persisted SessionResult (exerciseResults + volumeByMuscle), never
// re-rolling per-set laterality math.
//
// Supported here: muscle×hard-sets, muscle×tonnage, exercise×hard-sets,
// exercise×tonnage. `reps` is NOT supported (SessionResult persists no total
// rep count per exercise, only the top set) — the editor doesn't offer it.
// `pattern` is legacy (not offered by the editor).

// The threshold at/above which a muscle earns a FULL hard set. Below it a
// synergist still earns a proportional (fractional) credit — not zero — via
// resolveExerciseMuscleCredits (AP-6). Tonnage-by-muscle needs no threshold
// (it's already contribution-weighted).
export const HARD_SET_MUSCLE_CONTRIB_MIN = 0.5;

type TargetKey = Pick<TargetItem, "axis" | "key" | "metric">;

// Hard sets trained per muscle over the window [fromMs, toMs] (muscle slug →
// count). Attributes each exercise's persisted hardSetCount to every muscle it
// trains, at full credit for its substantial movers and fractional credit for
// its synergists (AP-6). Rotation-proof: back/hack/belt squat all add to
// `quadriceps`. Totals are fractional once synergists contribute — display
// rounds to ≤1dp. The window is passed as an explicit [from, to] range so the
// caller owns whether it's a calendar week (weekly targets) or anything else.
export const hardSetsByMuscle = (
  sessions: TimedSessionResult[],
  catalog: ReadonlyArray<Exercise>,
  fromMs: number,
  toMs: number,
  /** PG-16 — the catalog index, when the caller already built it. These three
   *  are called in a LOOP (one per muscle row, one per target row), and each
   *  rebuilt the same Map from the same unchanged catalog every time. Optional
   *  so the single-shot callers keep working untouched. */
  index?: ReadonlyMap<string, Exercise>,
): Record<string, number> => {
  const bySlug = index ?? catalogBySlug(catalog);
  const out: Record<string, number> = {};
  for (const s of sessions) {
    if (s.startedAtMs < fromMs || s.startedAtMs > toMs) continue;
    for (const er of s.result.exerciseResults) {
      const hs = er.hardSetCount ?? 0;
      if (hs <= 0) continue;
      const ex = bySlug.get(er.exerciseSlug);
      if (!ex) continue;
      for (const [m, credit] of resolveExerciseMuscleCredits(
        ex,
        HARD_SET_MUSCLE_CONTRIB_MIN,
      )) {
        out[m] = (out[m] ?? 0) + hs * credit;
      }
    }
  }
  return out;
};

// Slugs of exercises with hard sets in [fromMs, toMs] that the catalog lacks
// (distinct, sorted). hardSetsByMuscle credits them to no muscle, so a non-empty
// list means the muscle hard-set numbers may undercount. Same window and
// `hardSetCount > 0` filter as hardSetsByMuscle; counts are not changed.
export const uncataloguedExerciseSlugs = (
  sessions: TimedSessionResult[],
  bySlug: ReadonlyMap<string, Exercise>,
  fromMs: number,
  toMs: number,
): string[] => {
  const missing = new Set<string>();
  for (const s of sessions) {
    if (s.startedAtMs < fromMs || s.startedAtMs > toMs) continue;
    for (const er of s.result.exerciseResults) {
      if ((er.hardSetCount ?? 0) <= 0) continue;
      if (!bySlug.has(er.exerciseSlug)) missing.add(er.exerciseSlug);
    }
  }
  return [...missing].sort();
};

// #116 case 2: the exercises a session of [fromMs, toMs] lists in
// `bodyweightMissing`: computed with no bodyweight on the profile, their
// bodyweight sets counted zero volume and no hard set until the admin
// recompute runs. The compute decided them from the sets (`kind`), so nothing
// here guesses from catalog flags. Rows older than the field are not seen.
// Distinct, sorted.
export const bodyweightMissingSlugs = (
  sessions: TimedSessionResult[],
  fromMs: number,
  toMs: number,
): string[] => {
  const found = new Set<string>();
  for (const s of sessions) {
    if (s.startedAtMs < fromMs || s.startedAtMs > toMs) continue;
    for (const slug of s.result.bodyweightMissing ?? []) found.add(slug);
  }
  return [...found].sort();
};

// One session's contribution to a (axis, key, metric) — the shared routing so
// the buckets and the meter agree. Exported so the program-target PROJECTION
// (AP-10) can score a synthesised prescription session by the identical rule
// the logged actuals use, at single-session (per-day) granularity.
export const sessionTargetMetric = (
  s: TimedSessionResult,
  bySlug: ReadonlyMap<string, Exercise>,
  item: TargetKey,
): number => {
  if (item.axis === "muscle") {
    if (item.metric === "tonnage") {
      return normalizeVolumeByMuscle(s.result.volumeByMuscle)[
        item.key as CatalogMuscleSlug
      ] ?? 0;
    }
    if (item.metric === "hard-sets") {
      let n = 0;
      for (const er of s.result.exerciseResults) {
        const hs = er.hardSetCount ?? 0;
        if (hs <= 0) continue;
        const ex = bySlug.get(er.exerciseSlug);
        if (!ex) continue;
        const credit =
          resolveExerciseMuscleCredits(
            ex,
            HARD_SET_MUSCLE_CONTRIB_MIN,
          ).find(([m]) => m === (item.key as CatalogMuscleSlug))?.[1] ?? 0;
        n += hs * credit;
      }
      return n;
    }
    return 0;
  }
  if (item.axis === "exercise") {
    let n = 0;
    for (const er of s.result.exerciseResults) {
      if (er.exerciseSlug !== item.key) continue;
      if (item.metric === "tonnage") n += er.totalVolumeKg;
      else if (item.metric === "hard-sets") n += er.hardSetCount ?? 0;
    }
    return n;
  }
  return 0;
};

// The windowed actual over [fromMs, toMs] for a single target — the number the
// meter scores against `value`. Sums the SAME `sessionMetric` routing the editor
// reference uses, so meter and editor never disagree on what "actual" means for
// a (axis, key, metric). Muscle×hard-sets here equals hardSetsByMuscle for that
// key by construction (both route through resolveExerciseMuscleCredits).
export const weeklyActualFor = (
  sessions: TimedSessionResult[],
  catalog: ReadonlyArray<Exercise>,
  item: TargetKey,
  fromMs: number,
  toMs: number,
  /** PG-16 — the catalog index, when the caller already built it. These three
   *  are called in a LOOP (one per muscle row, one per target row), and each
   *  rebuilt the same Map from the same unchanged catalog every time. Optional
   *  so the single-shot callers keep working untouched. */
  index?: ReadonlyMap<string, Exercise>,
): number => {
  const bySlug = index ?? catalogBySlug(catalog);
  let n = 0;
  for (const s of sessions) {
    if (s.startedAtMs < fromMs || s.startedAtMs > toMs) continue;
    n += sessionTargetMetric(s, bySlug, item);
  }
  return n;
};

export type TargetActualStats = {
  // The CURRENT calendar week so far, the same partial week the meter scores.
  // ⚠️ Was called `lastWeek`, and it never was: it is this week, in progress.
  thisWeek: number;
  // PG-17: the last 4 COMPLETE calendar weeks, oldest → newest. The current
  // week is NOT one of them: two days of a Tuesday averaged with three full
  // weeks and divided by four dragged the "average week" down by construction.
  fullWeeks: number[];
  // fullWeeks total / 4: the average COMPLETE week. 0 when there is none.
  avgPerWeek: number;
  // Any nonzero in the complete weeks: the average is a real reference (else
  // "no full week yet", never an invented average).
  hasFullWeek: boolean;
  // Any nonzero in the complete weeks OR this one.
  hasData: boolean;
};

// The reference the editor + coverage show for a target: the average COMPLETE
// week + this week so far, for the selected (axis, key, metric). Buckets are
// CALENDAR weeks anchored to `weekStartDay`, the SAME boundary the meter scores
// on, so `thisWeek` equals the meter's current actual (PG-2). Default week
// start = Monday, so callers that don't thread the preference still bucket
// consistently. ⭐ PG-17, sua scelta del 25/09: la media esce dalle ultime
// quattro settimane PIENE, e la corrente sta a parte, dichiarata parziale.
export const weeklyTargetActualStats = (
  sessions: TimedSessionResult[],
  catalog: ReadonlyArray<Exercise>,
  item: TargetKey,
  nowMs: number,
  weekStartDay: number = DEFAULT_WEEK_START_DAY,
  /** PG-16 — the catalog index, when the caller already built it. These three
   *  are called in a LOOP (one per muscle row, one per target row), and each
   *  rebuilt the same Map from the same unchanged catalog every time. Optional
   *  so the single-shot callers keep working untouched. */
  index?: ReadonlyMap<string, Exercise>,
): TargetActualStats => {
  const bySlug = index ?? catalogBySlug(catalog);
  const fullWeeks = [0, 0, 0, 0];
  let thisWeek = 0;
  for (const s of sessions) {
    const k = calendarWeeksAgo(s.startedAtMs, nowMs, weekStartDay);
    if (k < 0 || k > 4) continue; // outside this week + the 4 complete ones
    const v = sessionTargetMetric(s, bySlug, item);
    if (v <= 0) continue;
    if (k === 0) thisWeek += v;
    else fullWeeks[4 - k] = (fullWeeks[4 - k] ?? 0) + v;
  }
  const fullTotal = fullWeeks.reduce((a, b) => a + b, 0);
  return {
    thisWeek,
    fullWeeks,
    avgPerWeek: fullTotal / 4,
    hasFullWeek: fullTotal > 0,
    hasData: fullTotal > 0 || thisWeek > 0,
  };
};
